/**
 * Exponential backoff with full jitter.
 *
 * delay = min(maxMs, baseMs * 2^attempt) then randomised in `[0, delay]`
 * ("full jitter", per the AWS architecture blog). Full jitter prevents the
 * thundering-herd retry storm that wakes every offline client the instant the
 * network returns. The caller resets `attempt` to 0 on an `online` event so a
 * recovered connection retries immediately rather than waiting out a long cap.
 */

import { validateNextBackoffMsArgs } from './validate-input'

export interface BackoffOptions {
	baseMs: number
	maxMs: number
	/** Injectable RNG for deterministic tests. Defaults to `Math.random`. */
	random?: () => number
}

/**
 * Compute the next retry delay (ms) for a given zero-based attempt count.
 *
 * @param attempt - Number of attempts already completed (0 → first retry).
 */
export function nextBackoffMs(attempt: number, opts: BackoffOptions): number {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateNextBackoffMsArgs(attempt, opts)
	const rng = opts.random ?? Math.random
	const safeAttempt = Math.max(0, Math.floor(attempt))
	// Cap the exponent so `2 ** n` cannot overflow to Infinity.
	const exponent = Math.min(safeAttempt, 53)
	const uncapped = opts.baseMs * 2 ** exponent
	const ceiling = Math.min(opts.maxMs, Number.isFinite(uncapped) ? uncapped : opts.maxMs)
	return Math.floor(rng() * ceiling)
}
